/**
 * AccountCardController — 슬롯 카드 Stimulus 컨트롤러
 *
 * 담당 기능:
 *   1. 마우스 위치 기반 3D 틸트 이펙트 (호버 시)
 *   2. 슬롯 파티클 폭발 효과 — 최초 활성화 / 새 슬롯 감지 시
 *
 * Values:
 *   slotId   {String}  — 슬롯 고유 식별자
 *   particle {Boolean} — true 이면 connect() 시 burst() 호출 (서버에서 주입)
 *
 * 사용법:
 *   data-controller="account-card"
 *   data-account-card-target="card"
 *   data-account-card-slot-id-value="12345"
 *   data-account-card-particle-value="true"   ← 파티클 트리거 시에만
 */
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  // ── 타겟 정의 ──────────────────────────────────────────
  static targets = ["card"]

  // ── 값 정의 ────────────────────────────────────────────
  static values = {
    slotId:   String,
    particle: Boolean   // 서버가 true 로 설정하면 카드 등장 시 파티클 발사
  }

  // ── 초기화 ────────────────────────────────────────────
  connect() {
    // 카드 요소에 perspective 설정 (3D 틸트용)
    this.cardTarget.style.perspective    = "1000px"
    this.cardTarget.style.transformStyle = "preserve-3d"
    this.cardTarget.style.transition     = "transform 0.1s ease-out"

    // 마우스 이벤트 바인딩 (나중에 removeEventListener 할 수 있도록 참조 보관)
    this._onMouseMove  = this.handleMouseMove.bind(this)
    this._onMouseLeave = this.handleMouseLeave.bind(this)
    this.cardTarget.addEventListener("mousemove",  this._onMouseMove)
    this.cardTarget.addEventListener("mouseleave", this._onMouseLeave)

    // particle 값이 true 이면 카드가 뷰에 붙은 뒤 파티클 발사
    // 400ms 딜레이: 카드 페이드인 애니메이션이 끝난 뒤 발사하기 위함
    if (this.particleValue) {
      setTimeout(() => this.burst(), 400)
    }
  }

  // ── 마우스 이동 핸들러 ────────────────────────────────
  // 마우스 위치 → 3D 틸트 CSS transform 계산
  handleMouseMove(e) {
    const card    = this.cardTarget
    const rect    = card.getBoundingClientRect()
    const centerX = rect.width  / 2
    const centerY = rect.height / 2
    const mouseX  = e.clientX - rect.left
    const mouseY  = e.clientY - rect.top

    // 정규화된 위치 (-1 ~ 1) → 틸트 각도 (-10 ~ +10도)
    const rotateX = -((mouseY - centerY) / centerY) * 10
    const rotateY =  ((mouseX - centerX) / centerX) * 10

    card.style.transform = `rotateX(${rotateX}deg) rotateY(${rotateY}deg)`
  }

  // ── 마우스 떠남 핸들러 ────────────────────────────────
  // 틸트 리셋 (0.3s 트랜지션으로 부드럽게)
  handleMouseLeave() {
    const card = this.cardTarget
    card.style.transition = "transform 0.3s ease-out"
    card.style.transform  = "rotateX(0deg) rotateY(0deg)"
    setTimeout(() => {
      card.style.transition = "transform 0.1s ease-out"
    }, 300)
  }

  // ── 파티클 폭발 ───────────────────────────────────────
  // 슬롯이 최초 활성화되거나 새로 생겨났을 때 호출.
  // 1) 카드 보더에 네온 글로우 펄스
  // 2) 카드 중심에서 28개 파티클이 사방으로 흩어짐
  burst() {
    const card = this.cardTarget
    const rect = card.getBoundingClientRect()

    // 카드 중심 좌표 (viewport 기준, 파티클 시작점)
    const cx = rect.left + rect.width  / 2
    const cy = rect.top  + rect.height / 2

    // ── 카드 네온 글로우 펄스 ─────────────────────────
    // 800ms 동안 강렬하게 빛난 뒤 1.5s 에 걸쳐 원래 상태로 복귀
    card.style.transition  = "box-shadow 0.3s ease, border-color 0.3s ease"
    card.style.boxShadow   = "0 0 60px rgba(57,255,20,0.8), 0 0 120px rgba(57,255,20,0.4), inset 0 0 30px rgba(57,255,20,0.1)"
    card.style.borderColor = "rgba(57,255,20,1)"

    setTimeout(() => {
      card.style.transition  = "box-shadow 1.5s ease, border-color 1.5s ease"
      card.style.boxShadow   = ""
      card.style.borderColor = ""
    }, 800)

    // ── 파티클 생성 ───────────────────────────────────
    const COUNT = 28  // 파티클 총 개수

    for (let i = 0; i < COUNT; i++) {
      // 원형 균등 분포: 각 파티클이 360° 를 COUNT 등분한 방향으로 날아감
      const angle = (i / COUNT) * Math.PI * 2
      const speed = 80 + Math.random() * 140  // 이동 거리 80~220px
      const dx    = Math.cos(angle) * speed   // X 이동량
      const dy    = Math.sin(angle) * speed   // Y 이동량

      // 파티클 크기: 2~8px 랜덤 (다양한 크기로 자연스러움 연출)
      const size     = 2 + Math.random() * 6
      // 50% 확률로 원형, 나머지 사각형 — 혼합으로 더 풍성하게
      const isCircle = Math.random() > 0.5

      const p = document.createElement("div")
      p.style.cssText = [
        "position:fixed",
        `left:${cx}px`,
        `top:${cy}px`,
        `width:${size}px`,
        `height:${size}px`,
        "background:#39FF14",
        isCircle ? "border-radius:50%" : "",
        `box-shadow:0 0 ${size * 2}px #39FF14`,
        "pointer-events:none",
        "z-index:99999",
        "transform:translate(-50%,-50%)",
        "animation:neon-particle-fly 1s ease-out forwards",
        // CSS @keyframes neon-particle-fly 에서 참조하는 커스텀 프로퍼티
        `--pdx:${dx}px`,
        `--pdy:${dy}px`,
      ].filter(Boolean).join(";")

      document.body.appendChild(p)

      // 애니메이션 종료(1s) 직후 DOM 에서 제거 (메모리 누수 방지)
      setTimeout(() => p.remove(), 1100)
    }
  }

  // ── 정리 ────────────────────────────────────────────
  // 이벤트 리스너 제거 (메모리 누수 방지)
  disconnect() {
    if (this.hasCardTarget) {
      this.cardTarget.removeEventListener("mousemove",  this._onMouseMove)
      this.cardTarget.removeEventListener("mouseleave", this._onMouseLeave)
    }
  }
}
